import React, { useState, useEffect } from 'react';
import { Contact, Country } from '../types';
import { X } from 'lucide-react';

interface AddContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: Contact) => void;
  initialData?: Contact;
}

const EXTRA_COUNTRIES = ['Zambia', 'Malawi', 'DRC', 'Ethiopia'];

const AddContactModal: React.FC<AddContactModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
  const initialFormState = {
    companyName: '',
    companyAddress: '',
    poBox: '',
    tin: '',
    vrn: '',
    email: '',
    contactPerson: '',
    phone: '',
    role: '',
    country: Country.TANZANIA as string // Allow string for extra countries
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          companyName: initialData.companyName,
          companyAddress: initialData.companyAddress,
          poBox: initialData.poBox,
          tin: initialData.tin,
          vrn: initialData.vrn,
          email: initialData.email,
          contactPerson: initialData.contactPerson,
          phone: initialData.phone,
          role: initialData.role,
          country: initialData.region || initialData.country || Country.TANZANIA
        });
      } else {
        setFormData(initialFormState);
      }
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const isExtraCountry = EXTRA_COUNTRIES.includes(formData.country);
    const finalCountry = isExtraCountry ? Country.TANZANIA : (formData.country as Country);
    const finalRegion = isExtraCountry ? formData.country : undefined;

    if (initialData) {
      const updatedContact: Contact = {
        ...initialData,
        ...formData,
        country: finalCountry,
        region: finalRegion
      };
      onSave(updatedContact);
    } else {
      const newContact: Contact = {
        id: Date.now().toString(),
        ...formData,
        country: finalCountry,
        region: finalRegion,
        lastContacted: 'Never',
        notes: '',
        sentiment: 'Neutral',
        avatarUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${formData.companyName}`,
      };
      onSave(newContact);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <div>
            <h3 className="font-black text-xl text-slate-800 uppercase tracking-tight">
                {initialData ? 'Modify Customer Profile' : 'Register New Customer'}
            </h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">CRM Database Entry</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-rose-600 transition-colors bg-white rounded-xl border border-slate-100">
             <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[75vh] custom-scrollbar">
          <div className="space-y-4">
            <h4 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest border-b border-indigo-50 pb-2">Corporate Identity</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Company Name</label>
                    <input
                        required
                        type="text"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 outline-none transition-all font-bold"
                        value={formData.companyName}
                        onChange={e => setFormData({...formData, companyName: e.target.value.toUpperCase()})}
                        placeholder="e.g. SHIV INTERNATIONAL LTD"
                    />
                </div>
                <div className="col-span-2">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Company Address</label>
                    <textarea
                        required
                        rows={2}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 outline-none transition-all text-sm resize-none"
                        value={formData.companyAddress}
                        onChange={e => setFormData({...formData, companyAddress: e.target.value})}
                        placeholder="Street, City, State..."
                    />
                </div>
                <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">P.O. Box</label>
                    <input
                        type="text"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 outline-none transition-all text-sm"
                        value={formData.poBox}
                        onChange={e => setFormData({...formData, poBox: e.target.value})}
                        placeholder="P.O. Box 1234"
                    />
                </div>
                <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Country Branch</label>
                    <select
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 outline-none transition-all text-sm font-bold appearance-none cursor-pointer"
                        value={formData.country}
                        onChange={e => setFormData({...formData, country: e.target.value})}
                    >
                        {Object.values(Country).map(c => (
                        <option key={c} value={c}>{c}</option>
                        ))}
                        {EXTRA_COUNTRIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest border-b border-indigo-50 pb-2">Tax Compliance</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">TIN (Tax ID)</label>
                    <input
                        type="text"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 outline-none transition-all text-sm font-mono"
                        value={formData.tin}
                        onChange={e => setFormData({...formData, tin: e.target.value})}
                        placeholder="000-000-000"
                    />
                </div>
                <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">VRN</label>
                    <input
                        type="text"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 outline-none transition-all text-sm font-mono"
                        value={formData.vrn}
                        onChange={e => setFormData({...formData, vrn: e.target.value})}
                        placeholder="VRN-0000X"
                    />
                </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest border-b border-indigo-50 pb-2">Direct Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Contact Person</label>
                    <input
                        required
                        type="text"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 outline-none transition-all text-sm font-bold"
                        value={formData.contactPerson}
                        onChange={e => setFormData({...formData, contactPerson: e.target.value})}
                        placeholder="Full Name"
                    />
                </div>
                <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Job Title / Role</label>
                    <input
                        required
                        type="text"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 outline-none transition-all text-sm"
                        value={formData.role}
                        onChange={e => setFormData({...formData, role: e.target.value})}
                        placeholder="e.g. Procurement Manager"
                    />
                </div>
                <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Phone No.</label>
                    <input
                        required
                        type="tel"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 outline-none transition-all text-sm font-bold"
                        value={formData.phone}
                        onChange={e => setFormData({...formData, phone: e.target.value})}
                        placeholder="+255..."
                    />
                </div>
                <div className="col-span-2">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Email Address</label>
                    <input
                        type="email"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 outline-none transition-all text-sm"
                        value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value.toLowerCase()})}
                        placeholder="name@company.com (Optional)"
                    />
                </div>
            </div>
          </div>

          <div className="pt-6 flex justify-end gap-3 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-100 rounded-2xl transition-all"
            >
              Discard
            </button>
            <button
              type="submit"
              className="px-8 py-3 text-[10px] font-black uppercase tracking-widest bg-indigo-600 text-white hover:bg-indigo-700 rounded-2xl shadow-xl shadow-indigo-100 transition-all active:scale-95"
            >
              {initialData ? 'Commit Update' : 'Finalize Registration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContactModal;